import * as Factory from 'factory.ts'
import faker from 'faker'
import { DateTime } from 'luxon'

import {
  type BudgetGroupSubmissionSchema,
  type BudgetSubmissionSchema,
} from '@webapp/api/BudgetCategoriesAPI'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import {
  type BudgetCategory,
  type BudgetCategoryAttributes,
  type BudgetCategoryDetails,
  type BudgetGroup,
  type BudgetGroupBudget,
} from '@webapp/models/BudgetCategory'
import {
  type BudgetIntervalType,
  BudgetIntervalTypes,
} from '@webapp/models/enums/BudgetInterval'
import {
  calculateNumberOfPeriods,
  generateBudgetForEntireDateRange,
  generateMonthlyBudgets,
  generateQuarterlyBudgets,
  generateYearlyBudgets,
} from '@webapp/utils/spend/generateBudgets'
import { AccountCodeMock } from './accountcode.factory'
import { DepartmentMock } from './department.factory'
import { UserMock } from './user.factory'

const mockStartDate = new Date('2021-01-01')

/**
 * Generates a budget category the UI generates when its being created or edited.
 * Note this is what the UI SENDS to the backend NOT what the UI receives from the backend.
 * @param interval
 * @param hasBudgets
 * @param hasCustomBudgets
 * @param includeIDs
 * @param budgetInfo
 * @returns BudgetGroupSubmissionSchema
 */
export const generateBudgetGroupForSubmission = (
  interval: 'monthly' | 'quarterly' | 'yearly' | null = null,
  hasBudgets = false,
  hasCustomBudgets = false,
  includeIDs = false,
  budgetInfo?: {
    startDateString: string
    endDateString: string
    timeZone?: string
    budgets?: BudgetSubmissionSchema[]
  }
): BudgetGroupSubmissionSchema => {
  const newStartDate =
    budgetInfo?.startDateString || faker.date.recent().toISOString()

  const startDate = DateTime.fromISO(newStartDate, {
    zone: budgetInfo?.timeZone,
  })

  const newEndDate =
    budgetInfo?.endDateString ||
    faker.date
      .between(newStartDate, startDate.plus({ months: 24 }).toISO())
      .toISOString()

  const endDate = DateTime.fromISO(newEndDate, { zone: budgetInfo?.timeZone })

  if (budgetInfo?.budgets && budgetInfo?.budgets.length > 0) {
    const budgetGroup: BudgetGroupSubmissionSchema = {
      interval,
      has_custom_budget: hasCustomBudgets,
      start_datetime: newStartDate,
      end_datetime: newEndDate,
      budgets: budgetInfo.budgets,
    }

    if (includeIDs) {
      budgetGroup.id = faker.random.number()
    }

    return budgetGroup
  }

  let budgets: BudgetSubmissionSchema[] = [
    {
      start_datetime: newStartDate,
      end_datetime: newEndDate,
      amount: null,
    },
  ]

  if (hasBudgets || hasCustomBudgets) {
    const amounts = []

    switch (interval) {
      case 'yearly':
        if (hasCustomBudgets) {
          const totalYears = calculateNumberOfPeriods(
            startDate,
            endDate,
            'yearly'
          )
          for (let index = 0; index < totalYears; index++) {
            amounts.push(faker.random.number())
          }
        } else {
          amounts.push(faker.random.number())
        }
        budgets = generateYearlyBudgets(startDate, endDate, amounts)
        break

      case 'quarterly':
        if (hasCustomBudgets) {
          const totalQuarters = calculateNumberOfPeriods(
            startDate,
            endDate,
            'quarterly'
          )
          for (let index = 0; index < totalQuarters; index++) {
            amounts.push(faker.random.number())
          }
        } else {
          amounts.push(faker.random.number())
        }
        budgets = generateQuarterlyBudgets(startDate, endDate, amounts)
        break

      case 'monthly':
        if (hasCustomBudgets) {
          const totalMonths = calculateNumberOfPeriods(
            startDate,
            endDate,
            'monthly'
          )
          for (let index = 0; index < totalMonths; index++) {
            amounts.push(faker.random.number())
          }
        } else {
          amounts.push(faker.random.number())
        }
        budgets = generateMonthlyBudgets(startDate, endDate, amounts)
        break

      default:
      case null:
        amounts.push(faker.random.number())
        budgets = generateBudgetForEntireDateRange(startDate, endDate, amounts)
        break
    }
  }

  if (includeIDs) {
    budgets.forEach(
      (budget, index): BudgetSubmissionSchema =>
        (budgets[index] = {
          id: faker.random.number(),
          ...budget,
        })
    )
  }

  const budgetGroups: BudgetGroupSubmissionSchema = {
    interval,
    has_custom_budget: hasCustomBudgets,
    start_datetime: newStartDate,
    end_datetime: newEndDate,
    budgets,
  }

  if (includeIDs) {
    budgetGroups.id = faker.random.number()
  }

  return budgetGroups
}

const newMockStartDateTime = faker.date.recent().toISOString()

export const BudgetCategoryMock = Factory.Sync.makeFactory<BudgetCategory>({
  id: Factory.each(() => faker.random.number()),
  uuid: Factory.each(() => faker.random.uuid()),
  name: Factory.each(() => faker.lorem.words()),
  budget: Factory.each(() => faker.random.number().toString()),
  approved: Factory.each(() => faker.random.number().toString()),
  invoiced: Factory.each(() => faker.random.number().toString()),
  received: Factory.each(() => faker.random.number().toString()),
  pending: Factory.each(() => faker.random.number().toString()),
  purchased: Factory.each(() => faker.random.number().toString()),
  start_datetime: newMockStartDateTime,
  end_datetime: Factory.each(() =>
    faker.date
      .between(
        newMockStartDateTime,
        DateTime.fromISO(newMockStartDateTime).plus({ months: 24 }).toISO()
      )
      .toISOString()
  ),
  attributes: Factory.each(() => BudgetCategoryAttributesMock.build()),
  users: Factory.each(() => UserMock.buildList(2)),
  source_of_truth: Factory.each(() => faker.random.boolean()),
  request_pending_amount: Factory.each(() => faker.random.number().toString()),
  interval: null,
  variance: Factory.each(() => faker.random.number().toString()),
  variance_percentage: Factory.each(() =>
    faker.finance.amount(0, 1, 2).toString()
  ),
})

export const BudgetCategoryAttributesMock =
  Factory.Sync.makeFactory<BudgetCategoryAttributes>({
    departments: Factory.each(() => DepartmentMock.buildList(2)),
    account_codes: Factory.each(() => AccountCodeMock.buildList(2)),
  })

/**
 * Mock factory for a budget object within a budget group.
 * Recommended that you use the GenerateBudgetGroupBudgetMocks or pass override values instead to ensure the data is sensical.
 */
export const BudgetGroupBudgetMock =
  Factory.Sync.makeFactory<BudgetGroupBudget>({
    id: Factory.each(() => faker.random.number()),
    amount: Factory.each(() => faker.random.number().toString()),
    approved: Factory.each(() => faker.random.number().toString()),
    pending: Factory.each(() => faker.random.number().toString()),
    purchased: Factory.each(() => faker.random.number().toString()),
    invoiced: Factory.each(() => faker.random.number().toString()),
    received: Factory.each(() => faker.random.number().toString()),
    request_pending_amount: Factory.each(() =>
      faker.random.number().toString()
    ),
    variance: Factory.each(() => faker.random.number().toString()),
    variance_percentage: Factory.each(() => faker.random.number().toString()),
    start_datetime: newMockStartDateTime,
    end_datetime: Factory.each(() =>
      faker.date
        .between(
          newMockStartDateTime,
          DateTime.fromISO(newMockStartDateTime).plus({ months: 24 }).toISO()
        )
        .toISOString()
    ),
  })

/**
 * Mock factory for a budget group object within a budget category.
 * Recommended that you use the GenerateBudgetGroupBudgetMocks or pass override values to ensure the data is sensical.
 */
export const BudgetGroupMock = Factory.Sync.makeFactory<BudgetGroup>({
  id: Factory.each(() => faker.random.number()),
  interval: null,
  has_custom_budget: false,
  start_datetime: newMockStartDateTime,
  end_datetime: Factory.each(() =>
    faker.date
      .between(
        newMockStartDateTime,
        DateTime.fromISO(newMockStartDateTime).plus({ months: 24 }).toISO()
      )
      .toISOString()
  ),
  budgets: [BudgetGroupBudgetMock.build()],
})

export const BudgetCategoryDetailsMock =
  Factory.Sync.makeFactory<BudgetCategoryDetails>({
    id: Factory.each(() => faker.random.number()),
    uuid: Factory.each(() => faker.random.uuid()),
    name: Factory.each(() => faker.lorem.words()),
    attributes: Factory.each(() => BudgetCategoryAttributesMock.build()),
    users: Factory.each(() => UserMock.buildList(2)),
    source_of_truth: Factory.each(() => faker.random.boolean()),
    budget_groups: Factory.each(() =>
      generateBudgetGroupMocks(faker.random.number(10))
    ),
  })

/**
 * Generates a number of budget group objects for a budget category that would be returned from the backend.
 * @param {number} numberOfBudgetGroups number of groups to generate
 * @returns BudgetGroup[]
 */
export const generateBudgetGroupMocks = (
  numberOfBudgetGroups = 1
): BudgetGroup[] => {
  // define budget group values that are consistent across all of the groups being generated
  const budgetGroups = []
  const interval: Omit<BudgetIntervalType, 'none'> | null =
    faker.random.arrayElement(['monthly', 'quarterly', 'yearly', null])
  const customBudgets = interval !== null ? faker.random.boolean() : false
  const hasBudgets = customBudgets ? true : faker.random.boolean()
  let startDate = mockStartDate
  let endDate = faker.date
    .between(
      startDate,
      DateTime.fromISO(startDate.toISOString()).plus({ months: 24 }).toISO()
    )
    .toISOString()

  // ensure budget groups don't have overlapping date ranges and generate each one and its budget objects
  for (let index = 0; index < numberOfBudgetGroups; index++) {
    if (index !== 0) {
      startDate = new Date(DateTime.fromISO(endDate).plus({ days: 1 }).toISO())
      endDate = faker.date
        .between(
          startDate,
          DateTime.fromISO(startDate.toISOString()).plus({ months: 24 }).toISO()
        )
        .toISOString()
    }
    // call budget mock and override everything except for id
    budgetGroups.push(
      BudgetGroupMock.build({
        interval,
        has_custom_budget: customBudgets,
        start_datetime: startDate.toISOString(),
        end_datetime: endDate,
        budgets: generateBudgetGroupBudgetMocks(
          interval,
          hasBudgets,
          customBudgets,
          startDate.toISOString(),
          endDate
        ),
      })
    )
  }

  return budgetGroups
}

export type SpendValues = {
  approved?: number
  pending?: number
  purchased?: number
  invoiced?: number
  requestPendingAmount?: number
}

/**
 * For a given budget group generates all the budget objects returned from the backend.
 * @param {Omit<BudgetIntervalType, 'none'> | null} interval - budget intervals (null is none or entire date range if hasBudgets is true)
 * @param {boolean} hasBudgets boolean if the budget group has a budget for each date range
 * @param {boolean} hasCustomBudgets boolean if each period within the date range has a unique budget.
 * @param {ISODateString} startDate startDate as an ISO string
 * @param {ISODateString} endDate endDate as an ISO string
 * @param {number[]} budgetValues array of budget amounts to be used
 * @param {SpendValues[]} spendValues array of spend value objects to be used
 * @returns BudgetGroupBudget[]
 */
export const generateBudgetGroupBudgetMocks = (
  interval: Omit<BudgetIntervalType, 'none'> | null,
  hasBudgets: boolean,
  hasCustomBudgets: boolean,
  startDate: ISODateString,
  endDate: ISODateString,
  timeZone?: TimezonesEnum,
  budgetValues?: number[],
  spendValues?: SpendValues[]
): BudgetGroupBudget[] => {
  const budgets = []
  // use schema type as generate budget funcs were made to return them
  let generatedBudgets: BudgetSubmissionSchema[] = []
  const startDateTime = DateTime.fromISO(startDate, { zone: timeZone })
  const endDateTime = DateTime.fromISO(endDate, { zone: timeZone })

  // calculate the number of intervals
  switch (interval) {
    case BudgetIntervalTypes.MONTHLY:
      generatedBudgets = generateMonthlyBudgets(startDateTime, endDateTime, [])
      break

    case BudgetIntervalTypes.QUARTERLY:
      generatedBudgets = generateQuarterlyBudgets(
        startDateTime,
        endDateTime,
        []
      )
      break

    case BudgetIntervalTypes.YEARLY:
      generatedBudgets = generateYearlyBudgets(startDateTime, endDateTime, [])
      break

    default:
    case null:
      generatedBudgets = generateBudgetForEntireDateRange(
        startDateTime,
        endDateTime,
        []
      )
      break
  }

  // if custom budgets generate a budget for each interval
  // if not custom and interval generate 1 budget to use for all intervals
  // if no interval and hasBudgets then above else no amount for all intervals
  let amount = null
  if (interval || (!interval && hasBudgets)) {
    amount = budgetValues ? budgetValues[0] : faker.random.number().toString()
  }

  generatedBudgets.forEach((budget, index) => {
    const { start_datetime, end_datetime } = budget
    if (hasCustomBudgets) {
      amount =
        budgetValues && budgetValues[index]
          ? budgetValues[index]
          : faker.random.number().toString()
    }

    const hasSpendValues = spendValues && spendValues[index]

    const approved =
      hasSpendValues && spendValues[index].approved
        ? spendValues[index].approved
        : faker.random.number()
    const pending =
      hasSpendValues && spendValues[index].pending
        ? spendValues[index].pending
        : faker.random.number()
    const purchased =
      hasSpendValues && spendValues[index].purchased
        ? spendValues[index].purchased
        : faker.random.number()
    const invoiced =
      hasSpendValues && spendValues[index].invoiced
        ? spendValues[index].invoiced
        : faker.random.number()
    const requestPendingAmount =
      hasSpendValues && spendValues[index].requestPendingAmount
        ? spendValues[index].requestPendingAmount
        : faker.random.number()
    const variance = amount ? amount - (approved + purchased + invoiced) : null
    const variancePercentage = amount ? (variance / amount) * 100 : null

    budgets.push(
      BudgetGroupBudgetMock.build({
        amount,
        start_datetime,
        end_datetime,
        approved: approved.toString(),
        pending: pending.toString(),
        purchased: purchased.toString(),
        invoiced: invoiced.toString(),
        request_pending_amount: requestPendingAmount.toString(),
        variance: variance && variance.toString(),
        variance_percentage:
          variancePercentage && variancePercentage.toString(),
      })
    )
  })

  return budgets
}
